import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#a4de6c'];

const StatsView = ({ statsData, statsError, fetchStats, darkMode, styles }) => {
    if (statsError) return (
        <div style={{padding: '40px', textAlign: 'center'}}>
            <AlertCircle size={48} color="#ff4d4d" style={{marginBottom: '10px'}} />
            <p style={{color: '#ff4d4d', marginBottom: '20px'}}>{statsError}</p>
            <button onClick={fetchStats} style={styles.retryBtn}>다시 시도</button>
        </div>
    );
    if (!statsData) return <div style={{padding: '40px', textAlign: 'center'}}>데이터 로딩 중...</div>;

    const analysis = statsData.analysis || {};

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{padding: '0 0 80px 0'}}
        >
            <h2 style={{fontSize: '20px', marginBottom: '20px', color: darkMode ? '#fff' : '#333'}}>통계 대시보드</h2>
            
            {/* Summary Cards */}
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '30px'}}>
                <div style={{padding: '20px', borderRadius: '15px', backgroundColor: darkMode ? '#333' : '#e3f2fd', color: darkMode ? '#fff' : '#0d47a1', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'}}>
                    <div style={{fontSize: '13px', opacity: 0.8, marginBottom: '5px'}}>총 물품</div>
                    <div style={{fontSize: '24px', fontWeight: 'bold'}}>{statsData.total_items}</div>
                </div>
                <div style={{padding: '20px', borderRadius: '15px', backgroundColor: darkMode ? '#333' : '#e8f5e9', color: darkMode ? '#fff' : '#1b5e20', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'}}>
                    <div style={{fontSize: '13px', opacity: 0.8, marginBottom: '5px'}}>총 수량</div>
                    <div style={{fontSize: '24px', fontWeight: 'bold'}}>{statsData.total_quantity}</div>
                </div>
                 <div style={{padding: '20px', borderRadius: '15px', backgroundColor: darkMode ? '#333' : '#fff3e0', color: darkMode ? '#fff' : '#e65100', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'}}>
                    <div style={{fontSize: '13px', opacity: 0.8, marginBottom: '5px'}}>최다 카테고리</div>
                    <div style={{fontSize: '18px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{analysis.top_category || '-'}</div>
                </div>
                <div style={{padding: '20px', borderRadius: '15px', backgroundColor: darkMode ? '#333' : '#f3e5f5', color: darkMode ? '#fff' : '#4a148c', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'}}>
                    <div style={{fontSize: '13px', opacity: 0.8, marginBottom: '5px'}}>주요 위치</div>
                    <div style={{fontSize: '18px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{analysis.top_location || '-'}</div>
                </div>
            </div>

            {/* Analysis Text */}
             <div style={{marginBottom: '30px', padding: '15px', borderRadius: '10px', backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa', fontSize: '14px', lineHeight: '1.6', border: darkMode ? '1px solid #444' : '1px solid #eee'}}>
                <p style={{margin: 0}}>
                    💡 현재 <strong>{analysis.top_location}</strong>에 가장 많은 물품이 보관되어 있으며, 
                    <strong>{analysis.top_category}</strong> 카테고리의 물품이 가장 많습니다.
                </p>
            </div>

            {/* Monthly Trend */}
            <div style={{marginBottom: '40px'}}>
                <h3 style={{fontSize: '16px', marginBottom: '20px', color: darkMode ? '#ccc' : '#666', borderLeft: '4px solid #FF8042', paddingLeft: '10px'}}>월별 등록 추이</h3>
                <div style={{height: '300px', width: '100%', backgroundColor: darkMode ? '#2a2a2a' : '#fff', borderRadius: '15px', padding: '10px'}}>
                    <ResponsiveContainer>
                        <LineChart data={statsData.monthly_stats} margin={{top: 5, right: 30, left: 20, bottom: 5}}>
                            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#444' : '#eee'} />
                            <XAxis dataKey="name" stroke={darkMode ? '#ccc' : '#666'} />
                            <YAxis stroke={darkMode ? '#ccc' : '#666'} />
                            <Tooltip contentStyle={{backgroundColor: darkMode ? '#333' : '#fff', borderColor: darkMode ? '#555' : '#ccc', color: darkMode ? '#fff' : '#000'}} />
                            <Legend />
                            <Line type="monotone" dataKey="value" name="등록 수" stroke="#8884d8" activeDot={{r: 8}} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Recent Activity */}
            <div style={{marginBottom: '40px'}}>
                <h3 style={{fontSize: '16px', marginBottom: '20px', color: darkMode ? '#ccc' : '#666', borderLeft: '4px solid #FFBB28', paddingLeft: '10px'}}>최근 활동 내역</h3>
                <div style={{backgroundColor: darkMode ? '#2a2a2a' : '#fff', borderRadius: '15px', padding: '15px', overflowX: 'auto'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '14px', color: darkMode ? '#eee' : '#333'}}>
                        <thead>
                            <tr style={{borderBottom: darkMode ? '1px solid #444' : '1px solid #eee', textAlign: 'left'}}>
                                <th style={{padding: '10px'}}>품명</th>
                                <th style={{padding: '10px'}}>카테고리</th>
                                <th style={{padding: '10px'}}>위치</th>
                                <th style={{padding: '10px'}}>수량</th>
                                <th style={{padding: '10px'}}>수정일</th>
                            </tr>
                        </thead>
                        <tbody>
                            {statsData.recent_activity && statsData.recent_activity.map((item) => (
                                <tr key={item.id} style={{borderBottom: darkMode ? '1px solid #333' : '1px solid #f9f9f9'}}>
                                    <td style={{padding: '10px', fontWeight: 'bold'}}>{item.name}</td>
                                    <td style={{padding: '10px'}}>{item.category || '-'}</td>
                                    <td style={{padding: '10px'}}>{item.location}</td>
                                    <td style={{padding: '10px'}}>{item.quantity}</td>
                                    <td style={{padding: '10px', fontSize: '12px', color: darkMode ? '#aaa' : '#888'}}>
                                        {new Date(item.updated_at).toLocaleDateString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div style={{marginBottom: '40px'}}>
                <h3 style={{fontSize: '16px', marginBottom: '20px', color: darkMode ? '#ccc' : '#666', borderLeft: '4px solid #0088FE', paddingLeft: '10px'}}>카테고리별 분포</h3>
                <div style={{height: '250px', width: '100%', backgroundColor: darkMode ? '#2a2a2a' : '#fff', borderRadius: '15px', padding: '10px'}}>
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie
                                data={statsData.category_stats}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                fill="#8884d8"
                                paddingAngle={5}
                                dataKey="value"
                            >
                                {statsData.category_stats.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip contentStyle={{backgroundColor: darkMode ? '#333' : '#fff', borderColor: darkMode ? '#555' : '#ccc', color: darkMode ? '#fff' : '#000'}} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div>
                <h3 style={{fontSize: '16px', marginBottom: '20px', color: darkMode ? '#ccc' : '#666', borderLeft: '4px solid #00C49F', paddingLeft: '10px'}}>위치별 분포</h3>
                <div style={{height: Math.max(300, statsData.location_stats.length * 40), width: '100%', backgroundColor: darkMode ? '#2a2a2a' : '#fff', borderRadius: '15px', padding: '10px'}}>
                    <ResponsiveContainer>
                        <BarChart data={statsData.location_stats} layout="vertical" margin={{top: 5, right: 30, left: 40, bottom: 5}}>
                            <XAxis type="number" hide />
                            <YAxis type="category" dataKey="name" width={80} tick={{fontSize: 12, fill: darkMode ? '#ccc' : '#666'}} />
                            <Tooltip contentStyle={{backgroundColor: darkMode ? '#333' : '#fff', borderColor: darkMode ? '#555' : '#ccc', color: darkMode ? '#fff' : '#000'}} />
                            <Bar dataKey="value" fill="#82ca9d" radius={[0, 4, 4, 0]}>
                                {statsData.location_stats.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </motion.div>
    );
};

export default StatsView;
